import React, { useEffect, useState } from "react";
import {
  ArrowLeft, Edit, Target,
  CheckCircle, Brain, Mic, BarChart3, Sparkles, Loader2, ArrowRightLeft,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { useParams, useNavigate } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import { useSocket } from "../../contexts/SocketContext";
import { useBranch } from "../../contexts/BranchContext";

// UI library
import { UserAvatar, StatMetric, Separator, StatusBadge, AppButton } from "../ui";

// Student sub-components
import StudentProfileCard from "./StudentProfileCard";
import StudentCourseBanner from "./StudentCourseBanner";
import StudentAICard from "./StudentAICard";
import StudentAssignmentCard from "./StudentAssignmentCard";
import StudentPayments from "./StudentPayments";
import StudentInterviews from "./StudentInterviews";
import StudentSelfPay from "./StudentSelfPay";
import TransferBranchModal from "../Common/TransferBranchModal";

import "./studentProfile.scss";

// ── Mini Donut (AI pass/fail indicator in stat card) ──
function MiniDonut({ passed, failed, size = 40 }) {
  const data = [
    { name: "Passed", value: passed || 0, fill: "#10b981" },
    { name: "Failed", value: failed || 0, fill: "#ef4444" },
  ];
  const total = (passed || 0) + (failed || 0);
  if (total === 0) return null;
  const rate = Math.round((passed / total) * 100);

  return (
    <div className="sp-mini-donut">
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={data}
            innerRadius="60%"
            outerRadius="100%"
            paddingAngle={2}
            dataKey="value"
            strokeWidth={0}
          >
            {data.map((e, i) => (
              <Cell key={i} fill={e.fill} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="sp-mini-donut__label">{rate}%</div>
    </div>
  );
}

// ═════════════════════════════════════════
// Main StudentProfile Component
// ═════════════════════════════════════════
const StudentProfile = () => {
  const loggedInUser = getCurrentUser();
  const params = useParams();
  const navigate = useNavigate();
  const readOnly = loggedInUser && loggedInUser.usertype === "student";
  const { socket } = useSocket();

  let id = params.id;
  if (readOnly) {
    id = loggedInUser.studentId;
  }

  const [student, setStudent] = useState(null);
  const [courseAssignments, setCourseAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interviews, setInterviews] = useState([]);
  const [feeHistory, setFeeHistory] = useState([]);
  const [dashStats, setDashStats] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showBranchTransfer, setShowBranchTransfer] = useState(false);
  const [err, setErr] = useState("");
  const [latestReport, setLatestReport] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const { isMultiBranch, branches } = useBranch();

  // ── Listen for class_started / class_ended to show/hide "Join Class" button ──
  useEffect(() => {
    if (!socket) return;

    const handleNotification = (data) => {
      if (!data || !data.batchId) return;
      const notifBatchId = String(data.batchId);

      if (data.type === 'class_started' || data.type === 'class_ended') {
        const isLive = data.type === 'class_started';

        // Update primary batch if it matches
        setStudent((prev) => {
          if (!prev) return prev;
          if (String(prev.batch?._id) === notifBatchId) {
            return { ...prev, batch: { ...prev.batch, isLive } };
          }
          return prev;
        });

        // Update additional course assignments if their batch matches
        setCourseAssignments((prev) =>
          prev.map((a) =>
            a.batchId && String(a.batchId._id) === notifBatchId
              ? { ...a, batchId: { ...a.batchId, isLive } }
              : a
          )
        );
      }
    };

    socket.on('notification', handleNotification);
    return () => socket.off('notification', handleNotification);
  }, [socket]);

  useEffect(() => {
    const fetchStudent = async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await apiClient.get(`/students/${id}`);
        setStudent(data.student);
        setCourseAssignments(data.courseAssignments || []);
      } catch {
        setErr("Failed to load student profile.");
      }
      setLoading(false);
    };

    const fetchInterviews = async () => {
      try {
        const { data } = await apiClient.get(`/interviews/student/${id}`);
        setInterviews(data.interviews || []);
      } catch {
        setInterviews([]);
      }
    };

    const fetchFees = async () => {
      try {
        const { data } = await apiClient.get(`/fees/student/${id}`);
        setFeeHistory(data.transactions || []);
      } catch {
        setFeeHistory([]);
      }
    };

    const fetchDashboardStats = async () => {
      try {
        const { data } = await apiClient.get(`/dashboard/student/${id}`);
        setDashStats(data);
      } catch {
        setDashStats(null);
      }
    };

    const fetchLatestReport = async () => {
      try {
        const { data } = await apiClient.get(`/ai-report/latest/${id}`);
        setLatestReport(data.report);
      } catch {
        setLatestReport(null);
      }
    };

    fetchStudent();
    fetchInterviews();
    fetchFees();
    fetchDashboardStats();
    fetchLatestReport();
  }, [id]);

  /** Generate AI Report */
  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    try {
      await apiClient.post('/ai-report/generate', { studentId: id });
      // Poll for completion
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const { data } = await apiClient.get(`/ai-report/latest/${id}`);
          if (data.report && data.report.overallScore > 0) {
            setLatestReport(data.report);
            setGeneratingReport(false);
            clearInterval(poll);
          }
        } catch { /* continue polling */ }
        if (attempts >= 30) { // 60 seconds max
          setGeneratingReport(false);
          clearInterval(poll);
        }
      }, 2000);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to generate report.';
      alert(msg);
      if (err.response?.data?.report) {
        setLatestReport(err.response.data.report);
      }
      setGeneratingReport(false);
    }
  };

  /** Called after a successful online payment to refresh data. */
  const refreshStudentData = async () => {
    try {
      const [stuRes, feeRes, dashRes] = await Promise.all([
        apiClient.get(`/students/${id}`),
        apiClient.get(`/fees/student/${id}`),
        apiClient.get(`/dashboard/student/${id}`),
      ]);
      setStudent(stuRes.data.student);
      setCourseAssignments(stuRes.data.courseAssignments || []);
      setFeeHistory(feeRes.data.transactions || []);
      setDashStats(dashRes.data);
    } catch {
      // Silently ignore — user already saw success toast
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="sp-page">
        <div className="sp-loading">
          <div className="sp-loading-spinner" />
          <span>Loading student profile...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (err || !student) {
    return (
      <div className="sp-page">
        <div className="sp-loading">
          <span>{err || "Student not found."}</span>
        </div>
      </div>
    );
  }

  // Compute stats from dashboard API data
  const courseCompletion = dashStats?.courseCompletion?.percent || 0;
  const attendance = dashStats?.attendance?.percent || 0;
  const aiTotal = dashStats?.ai?.totalQuestions || 0;
  const aiPassed = dashStats?.ai?.passed || 0;
  const aiFailed = dashStats?.ai?.failed || 0;
  const aiAvgScore = dashStats?.ai?.avgScore || 0;
  const mockTotal = dashStats?.mockInterview?.totalInterviews || 0;
  const mockAvg = dashStats?.mockInterview?.avgScore || 0;
  const mockHigh = dashStats?.mockInterview?.highestScore || 0;

  return (
    <div className="student-profile-page">
      {/* ── Sticky Top Bar (hidden for student users) ── */}
      {!readOnly && (
        <div className="sp-topbar">
          <div className="sp-topbar-left">
            <button className="sp-topbar-back" onClick={() => navigate(-1)}>
              <ArrowLeft size={16} />
            </button>
            <Separator orientation="vertical" className="sp-topbar-sep" />
            <div className="sp-topbar-info">
              <UserAvatar name={student.name} size="sm" />
              <div>
                <div className="sp-topbar-name">{student.name}</div>
                <div className="sp-topbar-batch">{student.batch?.name || "—"}</div>
              </div>
            </div>
          </div>
          <div className="sp-topbar-right">
            {student.batch?.isLive && (
              <button
                className="btn btn-sm sp-live-btn"
                onClick={() => (window.location.href = `/s/classroom/${student.batch._id}`)}
              >
                <span className="sp-live-dot" />
                Join Class
              </button>
            )}
            {isMultiBranch && branches.length > 1 && (
              <AppButton
                variant="outline"
                size="sm"
                onClick={() => setShowBranchTransfer(true)}
              >
                <ArrowRightLeft size={14} />
                Change Branch
              </AppButton>
            )}
            <AppButton
              variant="outline"
              size="sm"
              onClick={() => navigate(`/students/edit/${student._id}`)}
            >
              <Edit size={14} />
              Edit
            </AppButton>
          </div>
        </div>
      )}

      {/* ── Main Content ── */}
      <div className="sp-main-grid">
        {/* Left — Profile Sidebar */}
        <StudentProfileCard
          student={student}
          readOnly={readOnly}
          latestReport={latestReport}
          generatingReport={generatingReport}
          onGenerateReport={handleGenerateReport}
          onViewHistory={() => navigate('/s/ai-report/history')}
          onViewReport={(reportId) => navigate(`/s/ai-report/${reportId || ''}`)}
        />

        {/* Right — Stats + Course + Analytics + Lists */}
        <div className="sp-right-col">
          {/* KPI Stat Metrics */}
          <div className="sp-stats-grid">
            <StatMetric
              icon={<Target size={18} />}
              color="blue"
              value={`${courseCompletion}%`}
              label="Course Progress"
              progress={courseCompletion}
            />
            <StatMetric
              icon={<CheckCircle size={18} />}
              color="emerald"
              value={`${attendance}%`}
              label="Attendance"
              progress={attendance}
            />
            <StatMetric
              icon={<Brain size={18} />}
              color="violet"
              value={aiTotal > 0 ? `${aiTotal}` : "—"}
              suffix={aiTotal > 0 ? ` (${aiAvgScore}%)` : undefined}
              label="AI Attempts"
              extra={<MiniDonut passed={aiPassed} failed={aiFailed} />}
            />
            <StatMetric
              icon={<Mic size={18} />}
              color="rose"
              value={mockTotal > 0 ? `${mockTotal}` : "—"}
              suffix={mockTotal > 0 ? ` (${mockAvg}%)` : undefined}
              label="Mock Interviews"
              extra={
                mockTotal > 0 ? (
                  <StatusBadge status="active" className="sp-stat-badge">
                    Best: {mockHigh}%
                  </StatusBadge>
                ) : null
              }
            />
          </div>



          {/* Course + Fee Summary Banner + Pay Now */}
          <StudentCourseBanner
            student={student}
            courseAssignments={courseAssignments}
            onCollectFee={
              readOnly ? undefined : () => navigate(`/students/${id}/fees/new`)
            }
            onJoinClass={
              readOnly && student.batch?.isLive
                ? () => (window.location.href = `/s/classroom/${student.batch._id}`)
                : undefined
            }
            onPayNow={readOnly ? () => setShowPayModal(true) : undefined}
            readOnly={readOnly}
          />
          <StudentSelfPay
            student={student}
            show={showPayModal}
            onHide={() => setShowPayModal(false)}
            onPaymentSuccess={refreshStudentData}
          />

          {/* Analytics Cards */}
          <div className="sp-analytics-grid">
            <StudentAICard studentId={student._id} />
            <StudentAssignmentCard studentId={student._id} />
          </div>

          {/* Payments + Interviews Side by Side */}
          <div className="sp-bottom-grid">
            <StudentPayments
              payments={feeHistory}
              onViewAll={readOnly ? undefined : () => navigate(`/fees`)}
            />
            <StudentInterviews
              interviews={interviews}
              onSchedule={
                readOnly
                  ? () => navigate(`/s/interviews`)
                  : () => navigate(`/interviews`)
              }
            />
          </div>
        </div>
      </div>

      {/* Branch Transfer Modal */}
      <TransferBranchModal
        show={showBranchTransfer}
        onHide={() => setShowBranchTransfer(false)}
        selectedIds={[id]}
        mode="students"
        onTransfer={async () => {
          const { data } = await apiClient.get(`/students/${id}`);
          setStudent(data.student);
          setCourseAssignments(data.courseAssignments || []);
        }}
      />
    </div>
  );
};

export default StudentProfile;
